import { reactive, ref } from "vue";
import { POSITION, useToast } from "vue-toastification";

interface Comment {
  hashid: string;
  content: string;

  [key: string]: any;
}

interface PaginateState {
  data: Comment[];
  total: number;
}

export function usePageComment(team: any, page: any) {
  const toast = useToast();

  const pageNum = ref(1);
  const infiniteId = ref(new Date().getTime());
  const paginate = reactive<PaginateState>({ data: [], total: 0 });
  const isProcessing = ref(false);

  const fetch = async ($state?: any) => {
    if (isProcessing.value) {
      return;
    }

    if (!$state && paginate.data.length > 0) {
      paginate.data = [];
    }

    try {
      isProcessing.value = true;

      const { data } = await axios.get(route("app.ajax.pages.comments.index", [team, page]), {
        params: { page: pageNum.value },
      });

      if (data.data.length === 0) {
        $state?.complete();
      } else {
        paginate.data = [...paginate.data, ...data.data];
        paginate.total = data.meta.total;
        pageNum.value++;
        $state?.loaded();
      }
    } catch (error) {
      $state?.error();
    } finally {
      isProcessing.value = false;
    }
  };

  const createComment = (content: string, parent: Comment | undefined = undefined) => {
    content = content.trim();

    if (content.length < 1) {
      return;
    }

    return new Promise((resolve, reject) => {
      isProcessing.value = true;
      axios
        .post(route("app.ajax.pages.comments.store", [team, page]), {
          content: content,
          parent_id: parent ? parent.hashid : undefined,
        })
        .then(({ data }) => {
          const newComment = data.data;
          isProcessing.value = false;

          toast("댓글이 작성되었습니다.", {
            position: POSITION.BOTTOM_CENTER,
            timeout: 2000,
          });

          resolve(newComment);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          isProcessing.value = false;
        });
    });
  };

  const updateComment = async (comment: Comment) => {
    try {
      isProcessing.value = true;
      await axios.put(route("app.ajax.pages.comments.update", [team, page, comment]), {
        content: comment.text,
      });

      toast("댓글이 수정되었습니다.", {
        position: POSITION.BOTTOM_CENTER,
        timeout: 2000,
      });

      await reset();
    } finally {
      isProcessing.value = false;
    }
  };

  const deleteComment = async (comment: Comment) => {
    try {
      isProcessing.value = true;
      await axios.delete(route("app.ajax.pages.comments.destroy", [team, page, comment]));

      toast("댓글이 삭제되었습니다.", {
        position: POSITION.BOTTOM_CENTER,
        timeout: 2000,
      });

      await reset();
    } finally {
      isProcessing.value = false;
    }
  };

  const reset = async () => {
    infiniteId.value = new Date().getTime();
    pageNum.value = 1;
    paginate.data = [];
    paginate.total = 0;
    await fetch(null);
  };

  return {
    paginate,
    infiniteId,
    isProcessing,
    fetch,
    createComment,
    updateComment,
    deleteComment,
    reset,
  };
}
